/**
 * Layout reflow logic for sections.
 *
 * Each block carries an internal _style.layout slug.
 * The renderer reads this slug and adjusts the visual arrangement
 * of the section. This module provides:
 *   - sectionLayoutClasses(layout)  → wrapper className helpers
 *   - applyLayoutTransform(block, layout) → updates content fields to match layout
 */

export const sectionLayoutClasses = (layout = 'centered') => {
  switch (layout) {
    case 'left-aligned':
      return { container: 'text-left items-start', flex: 'flex-row', textAlign: 'left' };
    case 'right-aligned':
      return { container: 'text-right items-end', flex: 'flex-row-reverse', textAlign: 'right' };
    case 'split-50':
      return { container: '', flex: 'flex-row gap-12', textAlign: 'left', split: true };
    case 'image-top':
      return { container: 'text-center', flex: 'flex-col', textAlign: 'center', imageOrder: 1, textOrder: 2 };
    case 'image-bottom':
      return { container: 'text-center', flex: 'flex-col', textAlign: 'center', imageOrder: 2, textOrder: 1 };
    case 'overlay':
      return { container: 'text-center text-white', flex: '', textAlign: 'center', overlay: true };
    case 'narrow':
      return { container: 'text-left max-w-2xl mx-auto', flex: '', textAlign: 'left' };
    case 'centered':
    default:
      return { container: 'text-center', flex: 'flex-row', textAlign: 'center' };
  }
};

/** Persist alignment + image position in the block content so render reflects it. */
export const applyLayoutTransform = (block, layoutId) => {
  const c = block.content || {};
  const s = c._style || {};
  const next = { ...c, _style: { ...s, layout: layoutId } };

  switch (layoutId) {
    case 'left-aligned':
      next.alignment = 'left';
      next._style.alignment = 'left';
      break;
    case 'right-aligned':
      next.alignment = 'right';
      next._style.alignment = 'right';
      break;
    case 'centered':
      next.alignment = 'center';
      next._style.alignment = 'center';
      break;
    case 'image-top':
      next._style.imagePosition = 'top';
      break;
    case 'image-bottom':
      next._style.imagePosition = 'bottom';
      break;
    case 'split-50':
      next._style.imagePosition = 'left';
      next.alignment = 'left';
      break;
    case 'overlay':
      next._style.imagePosition = 'background';
      next.alignment = 'center';
      break;
    case 'narrow':
      next._style.maxWidth = 'narrow';
      next.alignment = 'left';
      break;
    default:
      break;
  }
  return next;
};

/** Map content-style alignment to actual style overrides so renderer picks them up. */
export const layoutStyleVars = (block) => {
  const c = block?.content || {};
  const s = c._style || {};
  const out = {};
  if (s.alignment) out.textAlign = s.alignment;
  if (s.maxWidth === 'narrow') out.maxWidth = '720px';
  return out;
};
